import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import {
  Box,
  Button,
  Drawer,
  Group,
  NativeSelect,
  SegmentedControl,
  Stack,
  Text,
  Textarea,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { createReference } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { IconFileTypePdf, IconPaperclip, IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { ReactElement, ReactNode, useRef, useState } from 'react';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { fetchBotWrite } from '@/services/botCalls';
import { ORBIT_DISABLED, ORBIT_GRAY } from '@/theme/colors';
import { candidateLine, ConnectCandidate } from '../ledger/ledgerConnect';
import { formatLedgerMoney } from '../ledger/ledgerRows';
import { usePatientCandidates } from '../ledger/usePatientCandidates';
import {
  canSubmit,
  draftAmount,
  emptyDraft,
  formatFileSize,
  MANUAL_BANK_ACCOUNTS,
  MANUAL_METHODS,
  ManualEntryDraft,
  MAX_PROOF_BYTES,
  occurredAt,
  readAsBase64,
  submitLabel,
} from './manualEntry';
import '../ledger/ledger.css';

/**
 * The prototype's Manual entry drawer (BAR-3031): money that reached the bank
 * outside every processor, with its PDF proof. The proof goes to the
 * `finance-proof` bot first, then the entry to the payments bot.
 */

const PATIENT_RESULT_COUNT = 5;

const SLOT_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 12px',
  border: '1px dashed rgba(0, 0, 0, 0.20)',
  borderRadius: 8,
  fontSize: 12,
  color: ORBIT_GRAY,
  width: '100%',
};

const PATIENT_BOX_STYLE = {
  border: '1px solid rgba(0, 0, 0, 0.10)',
  borderRadius: 10,
  padding: '10px 12px',
  background: 'var(--ev-color-warm-white, #fafcf7)',
};

function Field({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <Box>
      <Text fz={12} fw={500} c={ORBIT_GRAY} mb={4}>
        {label}
      </Text>
      {children}
    </Box>
  );
}

export interface ManualEntryDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** After the entry is written. */
  onAdded: () => void;
  /** Injectable for tests. */
  now?: Date;
}

export function ManualEntryDrawer({
  opened,
  onClose,
  onAdded,
  now = new Date(),
}: ManualEntryDrawerProps): ReactElement {
  const medplum = useMedplum();
  const [draft, setDraft] = useState<ManualEntryDraft>(() => emptyDraft(now));
  const [picked, setPicked] = useState<ConnectCandidate>();
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [debouncedQuery] = useDebouncedValue(query.trim(), 300);
  const {
    candidates,
    failed: candidatesFailed,
    retry: retryCandidates,
  } = usePatientCandidates(debouncedQuery, draftAmount(draft));

  const update = (change: Partial<ManualEntryDraft>): void => setDraft((current) => ({ ...current, ...change }));

  const close = (): void => {
    setDraft(emptyDraft(now));
    setPicked(undefined);
    setQuery('');
    onClose();
  };

  const attach = (file: File | undefined): void => {
    if (!file) return;
    if (file.type !== 'application/pdf' || file.size > MAX_PROOF_BYTES) {
      alert.error('The proof must be a PDF of at most 4 MB');
      return;
    }
    update({ proof: file });
  };

  const submit = async (): Promise<void> => {
    const { patient, proof } = draft;
    if (!patient || !proof) return;
    setSaving(true);
    try {
      const { binaryId } = await fetchBot<{ binaryId: string }>(medplum, 'finance-proof', {
        operation: 'upload',
        fileName: proof.name,
        contentType: proof.type,
        data: await readAsBase64(proof),
      });
      // On a failure the history re-reads: an entry that timed out may still have landed (BAR-3125).
      await fetchBotWrite(
        medplum,
        'create-treatment-payment',
        {
          operation: 'addManualLedgerEntry',
          manualEntry: {
            direction: draft.direction,
            method: draft.method,
            bankAccount: draft.bankAccount,
            occurredAt: occurredAt(draft),
            amount: draftAmount(draft),
            proofBinaryId: binaryId,
            proofFileName: proof.name,
            note: draft.note.trim() || null,
            patient: createReference(patient),
          },
        },
        onAdded
      );
      alert.success({
        message: `${draft.direction === 'payment' ? 'Payment' : 'Refund'} of ${formatLedgerMoney(
          draftAmount(draft)
        )} added to Revenue for ${formatGivenFamilyName(patient.name?.[0])}`,
      });
      onAdded();
      close();
    } catch (error) {
      alert.error(`Couldn't add the entry: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer opened={opened} onClose={close} position="right" size={440} title="Manual entry">
      <Stack gap={10}>
        <Field label="Type">
          <SegmentedControl
            size="xs"
            data={[
              { label: 'Payment', value: 'payment' },
              { label: 'Refund', value: 'refund' },
            ]}
            value={draft.direction}
            onChange={(direction) => update({ direction: direction as ManualEntryDraft['direction'] })}
          />
        </Field>
        <NativeSelect
          label="Method"
          size="xs"
          data={[...MANUAL_METHODS]}
          value={draft.method}
          onChange={(event) => update({ method: event.currentTarget.value as ManualEntryDraft['method'] })}
        />
        <NativeSelect
          label="Bank account"
          size="xs"
          data={[...MANUAL_BANK_ACCOUNTS]}
          value={draft.bankAccount}
          onChange={(event) => update({ bankAccount: event.currentTarget.value as ManualEntryDraft['bankAccount'] })}
        />
        <TextInput
          label="Date"
          size="xs"
          type="date"
          max={dayjs(now).format('YYYY-MM-DD')}
          value={draft.date}
          onChange={(event) => update({ date: event.currentTarget.value })}
        />
        <TextInput
          label="Amount"
          size="xs"
          inputMode="decimal"
          placeholder="0.00"
          value={draft.amount}
          onChange={(event) => update({ amount: event.currentTarget.value })}
        />
        <Field label="Patient">
          {draft.patient && picked ? (
            <Box style={PATIENT_BOX_STYLE}>
              <Group justify="space-between" gap={8}>
                <Text fz={13} fw={600}>
                  {formatGivenFamilyName(draft.patient.name?.[0])}
                </Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={() => {
                    update({ patient: undefined });
                    setPicked(undefined);
                    setQuery('');
                  }}
                >
                  Change
                </Button>
              </Group>
              <Text fz={12} c={ORBIT_DISABLED}>
                {candidateLine(picked)}
              </Text>
            </Box>
          ) : (
            <Stack gap={4}>
              <TextInput
                size="xs"
                type="search"
                placeholder="Search patients"
                leftSection={<IconSearch size={14} />}
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
              />
              {candidatesFailed && (
                <Group gap={8} role="alert">
                  <Text fz={12} c={ORBIT_DISABLED}>
                    Couldn't load patients.
                  </Text>
                  <UnstyledButton fz={12} fw={500} onClick={retryCandidates}>
                    Retry
                  </UnstyledButton>
                </Group>
              )}
              {candidates.slice(0, PATIENT_RESULT_COUNT).map((candidate) => (
                <UnstyledButton
                  key={candidate.patient.id}
                  className="ledger-connect-option"
                  style={{ padding: '4px 6px', borderRadius: 6 }}
                  onClick={() => {
                    update({ patient: candidate.patient });
                    setPicked(candidate);
                  }}
                >
                  <Text fz={13} fw={500}>
                    {formatGivenFamilyName(candidate.patient.name?.[0])}
                  </Text>
                  <Text fz={12} c={ORBIT_DISABLED}>
                    {candidateLine(candidate)}
                  </Text>
                </UnstyledButton>
              ))}
            </Stack>
          )}
        </Field>
        <Field label="Proof">
          <input
            ref={fileInput}
            type="file"
            accept="application/pdf"
            hidden
            data-testid="proof-input"
            onChange={(event) => attach(event.currentTarget.files?.[0])}
          />
          <UnstyledButton style={SLOT_STYLE} onClick={() => fileInput.current?.click()}>
            {draft.proof ? (
              <>
                <IconFileTypePdf size={14} />
                {`${draft.proof.name} · ${formatFileSize(draft.proof.size)}`}
              </>
            ) : (
              <>
                <IconPaperclip size={14} />
                Attach a PDF
              </>
            )}
          </UnstyledButton>
        </Field>
        <Textarea
          label="Note"
          size="xs"
          autosize
          minRows={3}
          value={draft.note}
          onChange={(event) => update({ note: event.currentTarget.value })}
        />
        <Group justify="flex-end" gap={8} mt={6}>
          <Button size="xs" variant="default" onClick={close}>
            Cancel
          </Button>
          <Button
            size="xs"
            color="dark"
            loading={saving}
            disabled={!canSubmit(draft, now)}
            onClick={() => void submit()}
          >
            {submitLabel(draft)}
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}
